import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { API } from '@/api'
import ArrowRight from '@/assets/icons/arrow-right.svg?react'
import { Button, ButtonRadio, Loading, Trans } from '@/components'
import { Pagination } from '@/components/base/Pagination'
import { BILL_SUBTYPE_DISPLAY, BILL_TYPE_DISPLAY } from '@/models/bill'
import { useUserStore } from '@/stores/user'
import { formatDatePreset, formatDurationToHours, formatPrice } from '@/utils/formatter'
import { cn } from '@/utils/style'

import { ExchargeModal } from './components/RechargeModal'

export function BillingPage() {
  const [loading, setLoading] = useState(false)
  const [catalog, setCatalog] = useState(0)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [bills, setBills] = useState<any[]>([])
  const pageSize = 10
  const userStore = useUserStore()
  const { t } = useTranslation()
  const [exchargeModalOpen, setExchargeModalOpen] = useState(false)

  const catalogItems = useMemo(() => {
    return [
      {
        label: t('billing.type.all'),
        value: 0,
      },
      {
        label: t('billing.type.instances'),
        value: 1,
      },
      // {
      //   label: 'Bits',
      //   value: -1,
      //   disabled: true,
      // },
      {
        label: t('billing.type.others'),
        value: 2,
      },
    ]
  }, [])

  const overviewItems = useMemo(() => {
    return [
      {
        key: 'balance',
        label: t('billing.balanceTitle'),
        value: formatPrice(userStore.account.totalBonus.moneyCent ?? 0, false, ''),
        action: () => {
          setExchargeModalOpen(true)
        },
      },
      {
        key: 'total-spending',
        label: t('billing.totalSpending'),
        value: formatPrice(userStore.account.expense.amount ?? 0, false, ''),
      },
      // {
      //   key: 'bit-balance',
      //   label: 'My Bits (Bit)',
      //   value: '-',
      // },
      {
        key: 'total-duration',
        label: t('billing.duration'),
        value: formatDurationToHours(userStore.account.expense.timeSec ?? 0),
      },
      // {
      //   key: 'invoicing-limit',
      //   label: t('billing.invoicingLimit'),
      //   value: '-',
      //   disabled: true,
      //   action: () => {},
      // },
    ]
  }, [userStore])

  const fetchBill = async (p = page) => {
    setLoading(true)
    try {
      const res = await API.getUserBills({
        page: p,
        per_page: pageSize,
        catalog: catalog,
      })
      if (res.code === 0) {
        setBills(
          res.bills?.map((b: any) => {
            const { pointsCent } = b.Info?.RechargeInfo || {}

            const typeStr =
              t(
                `billing.typeStr.${BILL_SUBTYPE_DISPLAY[b.subType] || BILL_TYPE_DISPLAY[b.type] || '-'}`,
              ) || '-'
            const paymentStr =
              b.Info !== null && b.Info.RechargeInfo
                ? `${(pointsCent / 100).toFixed(3)} ${Math.abs(Number((pointsCent / 100).toFixed(3))) > 1 ? 'Credits' : 'Credit'}`
                : typeStr === 'Recharge' && b.Info === null
                  ? formatPrice(b.amount, false, Math.abs(b.amount) > 100 ? 'Credits' : 'Credit')
                  : formatPrice(b.amount, false, Math.abs(b.amount) > 100 ? 'Credits' : 'Credit')

            return {
              ...b,
              isUnsettled: b.type === 1,
              typeStr,
              paymentStr,
            }
          }) || [],
        )

        setPage(p)
        setTotal(res.pagination.total)
      }
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchBill()
  }, [catalog])

  return (
    <div className="flex flex-col h-full">
      <div className="card p-4 overflow-clip bg-white">
        <div className="grid grid-cols-3 text-secondary gap-x-4">
          {overviewItems.map((item) => (
            <div
              key={item.key}
              className={cn(
                'border border-[#F2F5F8] p-6 rounded-2xl',
                item.action ? ' cursor-pointer' : '',
                { 'cursor-not-allowed': false },
              )}
              onClick={item.action}
            >
              <div className="t-x-sb text-secondary mb-2">{item.label}</div>
              <div className="flex items-center gap-x-4">
                <div className="h5 text-primary flex-grow">{item.value}</div>
                {item.action && <ArrowRight width={20} height={20} />}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="card bg-panel-white h-full overflow-y-auto mt-4">
        <div className="flex gap-x-8 h6 items-start justify-between border-b border-[#E1E4EA] pb-4 mb-12">
          <h6 className="text-primary">
            <Trans i18nKey="billing.billingTab">My Spending</Trans>
          </h6>
          <Button type="secondary" onClick={() => setExchargeModalOpen(true)}>
            <Trans i18nKey="billing.rechargeBtn">Recharge</Trans>
          </Button>
        </div>

        <div className="mb-6">
          <ButtonRadio
            items={catalogItems}
            unselectedClassName={'text-tertiary'}
            onChange={(v) => {
              setPage(1)
              setCatalog(v)
            }}
          />
        </div>

        {loading ? (
          <div className="w-full flex items-center justify-center p-8">
            <Loading />
          </div>
        ) : (
          <>
            <table className="table mt-4">
              <thead>
                <tr>
                  <th>
                    <Trans i18nKey="profile.billingTableHeaderType">Time</Trans>
                  </th>
                  <th>
                    <Trans i18nKey="profile.billingTableHeaderName">Name</Trans>
                  </th>
                  <th>
                    <Trans i18nKey="profile.billingTableHeaderStatus">Status</Trans>
                  </th>

                  <th className="!text-right">
                    <Trans i18nKey="profile.billingTableHeaderPayment">Payment</Trans>
                  </th>
                </tr>
              </thead>
              <tbody>
                {bills.map((data, idx) => (
                  <tr key={idx}>
                    <td>
                      <div className="t-sb text-secondary">
                        {data.isUnsettled
                          ? '-'
                          : formatDatePreset(data.billTime.seconds * 1000, 'second')}
                      </div>
                    </td>
                    <td>
                      <div className="text-primary t-sb">{data.typeStr}</div>
                    </td>
                    <td
                      className={cn('t-sb', {
                        'text-highlight': data.isUnsettled,
                        'text-success': !data.isUnsettled,
                      })}
                    >
                      {data.isUnsettled ? t('billing.status.ongoing') : t('billing.status.settled')}
                    </td>
                    <td className="text-right t-sb">{data.paymentStr}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination
              size={pageSize}
              total={total}
              current={page}
              className="justify-end mt-4"
              onChange={(page) => fetchBill(page)}
            />
          </>
        )}
      </div>

      {exchargeModalOpen && (
        <ExchargeModal
          onOk={() => {
            setExchargeModalOpen(false)
            fetchBill()
          }}
          onClose={() => {
            setExchargeModalOpen(false)
          }}
        />
      )}
    </div>
  )
}
